import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  IconButton,
  Icons,
  Divider,
  useTheme,
  Chip,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import TimeAgo from 'react-timeago';
import { COMMON_ROOM } from '@core/constants/postFeedType';
import CommentsEntityTypes from '@core/utils/commentsEntityTypes';

import Reactions from '@components/presentational/Reactions/Reactions.container';
import CommentListCollapsable from '@components/presentational/Comments/CommentListCollapsable/CommentListCollapsable';
import { PostDetailPopupSkeleton } from './RecentUpdatesSkeleton';
import {
  HomeCreateComment,
  HomeEditorReadOnly,
} from './PostDetailPopup.connectors';
import {
  timeFormatter,
  getFeedLabel,
  isAnnouncementPost,
} from './recentUpdates.utils';

export interface PostDetailViewProps {
  onClose?: () => void;
  item: any;
  post: any | null;
  comments: any[];
  isLoading: boolean;
  isReadOnly: boolean;
  showComments: boolean;
  scrollTo?: string;
  fullName: string;
  avatarUrl: string;
  userInitials: string;
  classRoomName: string | undefined;
  classRoomStartDate: string | undefined;
  classRoomEndDate: string | undefined;
  toggleReaction: (args: { emojiName: string }) => void;
  onDidComment: () => void;
  isEducatorPost: boolean;
}

const PostDetailView: React.FC<PostDetailViewProps> = ({
  onClose,
  item,
  post,
  comments,
  isLoading,
  isReadOnly,
  showComments,
  fullName,
  avatarUrl,
  userInitials,
  classRoomName,
  toggleReaction,
  onDidComment,
  isEducatorPost,
  scrollTo,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const feedLabel = getFeedLabel(item?.feedType);
  const isAnnouncement = isAnnouncementPost(item?.feedType);

  const spaceId = item?.classRoom;
  const postId = item?.objectId || item?.id;
  const content = post?.content ? JSON.parse(post.content) : null;
  const isContentReady = !isLoading && post && content;

  const reactionsRef = React.useRef<HTMLDivElement>(null);
  const commentsRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (isContentReady && scrollTo) {
      setTimeout(() => {
        if (scrollTo === 'reactions' && reactionsRef.current) {
          reactionsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        } else if (scrollTo === 'comments' && commentsRef.current) {
          commentsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 150);
    }
  }, [isContentReady, scrollTo]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
        width: '100%',
        bgcolor: theme.palette.background.paper,
        borderRadius: 2,
        ...(isSmallScreen && {
          boxShadow: '0px 8px 16px 0px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${theme.palette.divider}`,
        }),
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          px: 3,
          pt: 2.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {/* Feed type chip */}
            <Chip
              label={feedLabel}
              sx={{
                bgcolor: isEducatorPost
                  ? theme.palette.secondary.main
                  : theme.palette.primary.light,
                color: theme.palette.secondary.contrastText,
              }}
            />
          </Box>
          {classRoomName && (
            <Typography
              variant="subtitle2"
              color="textSecondary"
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {classRoomName}
            </Typography>
          )}
        </Box>

        {!isSmallScreen && onClose && (
          <IconButton
            onClick={onClose}
            aria-label="Close post detail"
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': { color: theme.palette.text.primary },
            }}
          >
            <Icons.Close fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2, overflowX: 'hidden' }}
      >
        {!isContentReady ? (
          <PostDetailPopupSkeleton />
        ) : (
          <Box
            sx={{
              '@keyframes fadeSlideIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
              },
              animation: 'fadeSlideIn 300ms ease-out both',
            }}
          >
            {/* Author */}
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <Avatar
                src={avatarUrl}
                fallbackText={userInitials}
                sx={{ width: 44, height: 44 }}
                aria-hidden="true"
              />
              <Box>
                <Typography
                  variant="body1"
                  fontWeight="medium"
                  color="textPrimary"
                >
                  {fullName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  <TimeAgo date={item?.createdAt} formatter={timeFormatter} />
                </Typography>
              </Box>
            </Box>

            {/* Post body */}
            <Box sx={{ mb: 2 }}>
              <HomeEditorReadOnly
                key={post.editedAt || post.createdAt}
                itemId={post.objectId}
                rawEditorState={content}
                itemType={post.feedType || COMMON_ROOM}
                spaceId={spaceId}
              />
              {post.editedAt && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  (Edited)
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Reactions */}
            <Box ref={reactionsRef}>
              <Reactions
                sequenceId={`home-post-${postId}`}
                itemId={postId}
                toggleReaction={toggleReaction}
                style={{ marginBottom: 15 }}
                readOnly={isReadOnly}
              />
            </Box>

            {/* Comments — hidden for Announcements */}
            {!isAnnouncement && (
              <Box sx={{ width: '100%' }} ref={commentsRef}>
                <CommentListCollapsable
                  entityId={postId}
                  entityType={CommentsEntityTypes.post}
                  itemType={post.feedType || COMMON_ROOM}
                  showComments={showComments}
                  showInitialComments
                  comments={comments}
                  commentsCount={comments.length}
                  spaceId={spaceId}
                />
                {!isReadOnly && postId && (
                  <HomeCreateComment
                    entityId={postId}
                    entityType={CommentsEntityTypes.post}
                    didComment={onDidComment}
                    spaceId={spaceId}
                  />
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PostDetailView;
